import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Flame, Trophy, MapPin, CalendarDays } from "lucide-react";
import { fetchPackages, inr } from "@/lib/api";

const dur = (p) => (p.days ? `${p.days}D/${p.nights}N` : p.duration_label || "");
const priceOf = (p) => (p.enquiry_only || p.starting_price == null ? "On enquiry" : `from ${inr(p.starting_price)}`);

function usePublishedPackages() {
    const [pkgs, setPkgs] = useState(null);
    useEffect(() => {
        fetchPackages().then((r) => setPkgs((r.packages || []).filter((p) => p.status === "published"))).catch(() => setPkgs([]));
    }, []);
    return pkgs;
}

/* ---------- 1. Package strip of ALL package types — right below the hero ---------- */
export function PackageHighlights() {
    const pkgs = usePublishedPackages();
    if (!pkgs || !pkgs.length) return null;
    return (
        <section className="bg-cream border-b border-stone-200 py-16 sm:py-20" data-testid="home-highlights">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-end justify-between gap-3 mb-10">
                    <div>
                        <p className="font-script text-3xl sm:text-4xl text-saffron -rotate-1 inline-block leading-none">tour packages</p>
                        <h2 className="mt-1 font-display font-black text-3xl sm:text-4xl text-navy tracking-tight">Where can we take you?</h2>
                    </div>
                    <Link to="/packages" data-testid="highlights-view-all" className="shrink-0 inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-saffron hover:text-saffron-dark">
                        All packages <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
                <div className="flex gap-5 overflow-x-auto pb-3 snap-x snap-mandatory [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {pkgs.map((p) => (
                        <Link key={p.id || p.slug} to={`/package/${p.slug}`} data-testid={`highlight-card-${p.slug}`}
                            className="group snap-start shrink-0 w-64 sm:w-72 rounded-2xl bg-white border border-stone-300 overflow-hidden hover:border-saffron hover:shadow-xl hover:-translate-y-1 transition-all">
                            <div className="h-40 overflow-hidden bg-stone-100">
                                {p.cover_image && <img src={p.cover_image} alt={p.name} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                            </div>
                            <div className="p-5">
                                <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-wider text-stone-500">
                                    <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5 text-saffron" />{dur(p)}</span>
                                    {p.category && <span className="rounded-full bg-cream px-2.5 py-1 text-stone-500">{p.category}</span>}
                                </div>
                                <p className="mt-2 font-display font-black text-base text-navy leading-snug line-clamp-2">{p.name}</p>
                                <p className="mt-2 text-sm font-bold text-saffron">{priceOf(p)}</p>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ---------- 2. Best package spotlight + filling fast ---------- */
export function BestSpotlight() {
    const pkgs = usePublishedPackages();
    const { best, filling } = useMemo(() => {
        const all = pkgs || [];
        const best = all.find((p) => p.recommended) || all.find((p) => (p.badges || []).some((b) => /best|popular/i.test(b))) || all[0];
        const filling = all.filter((p) => p !== best && (p.badges || []).some((b) => /filling fast|few seats|high demand|selling/i.test(b)));
        return { best, filling };
    }, [pkgs]);
    if (!pkgs || !pkgs.length || !best) return null;

    return (
        <section className="bg-navy-deep" data-testid="home-bestspotlight">
            <div className="max-w-7xl mx-auto px-4 py-12">
                <div className="flex items-center gap-2 mb-1">
                    <Trophy className="h-7 w-7 sm:h-8 sm:w-8 text-gold" />
                    <p className="font-script text-3xl sm:text-4xl text-gold -rotate-1 inline-block leading-none">most loved right now</p>
                </div>
                <h2 className="font-display font-black text-2xl sm:text-3xl text-white">Best Package & Filling Fast</h2>

                <div className="mt-6 grid lg:grid-cols-5 gap-5">
                    <Link to={`/package/${best.slug}`} data-testid="best-package-card"
                        className="group lg:col-span-3 relative rounded-2xl overflow-hidden border border-white/10 hover:border-gold/60 transition-colors min-h-72 flex flex-col justify-end">
                        {best.cover_image && <img src={best.cover_image} alt={best.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                        <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy-deep/40 to-transparent" />
                        <span className="absolute top-4 left-4 inline-flex items-center gap-1 rounded-full bg-gold text-navy-deep px-3 py-1 text-[10px] font-black uppercase tracking-wider" data-testid="best-badge">
                            <Trophy className="h-3 w-3" /> Best package
                        </span>
                        <div className="relative p-5">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-gold flex items-center gap-1.5"><MapPin className="h-3 w-3" />{best.destination_name} · {dur(best)}</p>
                            <h3 className="mt-1 font-display font-black text-xl sm:text-2xl text-white leading-tight">{best.name}</h3>
                            <div className="mt-2 flex items-center gap-3 flex-wrap">
                                <span className="font-display font-black text-gold text-lg">{priceOf(best)}</span>
                                <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider text-white/90 group-hover:text-gold transition-colors">View itinerary <ArrowRight className="h-3.5 w-3.5" /></span>
                            </div>
                        </div>
                    </Link>

                    <div className="lg:col-span-2 rounded-2xl bg-white/5 border border-white/10 p-5" data-testid="filling-fast-panel">
                        <p className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-saffron-light"><Flame className="h-3.5 w-3.5" /> Filling fast</p>
                        <div className="mt-3 space-y-3">
                            {(filling.length ? filling : pkgs.filter((p) => p !== best).slice(0, 3)).map((p) => (
                                <Link key={p.id || p.slug} to={`/package/${p.slug}`} data-testid={`filling-card-${p.slug}`}
                                    className="flex items-center gap-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 p-2.5 transition-colors group">
                                    <div className="h-14 w-20 shrink-0 rounded-lg overflow-hidden bg-stone-700">
                                        {p.cover_image && <img src={p.cover_image} alt={p.name} loading="lazy" className="h-full w-full object-cover" />}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="font-display font-bold text-sm text-white leading-snug line-clamp-2">{p.name}</p>
                                        <p className="text-[10px] font-bold text-white/60 mt-0.5">{dur(p)} · {priceOf(p)}</p>
                                    </div>
                                    <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-saffron/15 text-saffron-light px-2 py-1 text-[9px] font-black uppercase tracking-wider">
                                        <Flame className="h-3 w-3" />{(p.badges || []).some((b) => /filling fast|few seats|high demand|selling/i.test(b)) ? "Filling fast" : "Trending"}
                                    </span>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ---------- 3. Elaborated package section — REMOVED (owner request 2026-10-26:
   it repeated packages already shown in the highlights strip + spotlight) ---------- */
